import { Description, DialogTitle, Radio, RadioGroup } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { format } from 'date-fns'
import { de, enUS as en } from 'date-fns/locale'
import { MotionConfig, Reorder, useDragControls } from 'framer-motion'
import PropTypes from 'prop-types'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import MatchingProfilePreview from '../../assets/images/strengthprofile-preview.png'
import StrengthProfilePreview from '../../assets/images/strengthprofile-preview1.png'
import {
  MATCHINGPROFILE,
  TALENTPROFILE
} from '../../constants/evaluation-types'
import { useLanguageContext } from '../../context'
import { classNames } from '../../helpers'
import { localStorage } from '../../helpers/local-storage'
import {
  BUNDLE_LANGUAGES,
  moveItem,
  parseStoredProfileOrder,
  sortProfileTypes
} from '../../helpers/pdf-bundle'
import GripIcon from '../grip-icon'
import Button from '../tailwind/Button'
import Modal from '../tailwind/Modal'
import { getFileTypeVisual } from './space-documents'

const I18N = 'pages.talent.'

// Die zuletzt gewaehlte Sprache gilt fuer den naechsten Export. Wer fuer ein
// englischsprachiges Team exportiert, will nicht jedes Mal umstellen.
const LANGUAGE_STORAGE_KEY = 'PDF_BUNDLE_LANGUAGE'

// Dasselbe fuer die Reihenfolge der Profile. Wer das Staerkenprofil vorne
// haben will, will das bei jedem Talent.
const PROFILE_ORDER_STORAGE_KEY = 'PDF_BUNDLE_PROFILE_ORDER'

const KEY_MOVES = { ArrowUp: -1, ArrowDown: 1 }

const BUNDLE_LANGUAGE_VALUES = BUNDLE_LANGUAGES.map(({ value }) => value)

const getInitialLanguage = (dashboardLanguage) => {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY)
  if (BUNDLE_LANGUAGE_VALUES.includes(stored)) return stored

  return BUNDLE_LANGUAGE_VALUES.includes(dashboardLanguage)
    ? dashboardLanguage
    : 'de'
}

// Farben und Vorschaubilder wie bei den Kacheln unter ANHAENGE, damit die
// Zeilen im Dialog wiedererkennbar sind.
const PROFILE_VISUALS = {
  [MATCHINGPROFILE]: {
    background: '#2C46B2',
    imageSrc: MatchingProfilePreview
  },
  [TALENTPROFILE]: { background: '#fe79a7', imageSrc: StrengthProfilePreview }
}

const toggle = (list, value) =>
  list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value]

// Nur das Label mit Checkbox. Das <li> drumherum setzt der Aufrufer, weil die
// Profilzeile ein Reorder.Item mit Griff daneben ist.
const OptionLabel = ({
  id,
  checked,
  disabled,
  onChange,
  visual,
  label,
  description
}) => (
  <label
    htmlFor={id}
    className={classNames(
      'flex min-w-0 flex-1 items-center gap-3 px-2 py-2',
      disabled ? 'cursor-default' : 'cursor-pointer'
    )}
  >
    <input
      id={id}
      type='checkbox'
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className='h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-1 focus:ring-blue-600 disabled:cursor-default disabled:opacity-50'
    />
    {visual}
    <span className='min-w-0 flex-1'>
      <span
        className={classNames(
          'block truncate text-sm font-medium',
          disabled ? 'text-gray-400' : 'text-gray-900'
        )}
        title={label}
      >
        {label}
      </span>
      {description && (
        <span className='block truncate text-xs text-gray-500'>
          {description}
        </span>
      )}
    </span>
  </label>
)

OptionLabel.propTypes = {
  id: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  visual: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string
}

OptionLabel.defaultProps = {
  disabled: false,
  description: undefined
}

/**
 * Profilzeile, die sich am Griff ziehen laesst. Gezogen wird nur am Griff,
 * sonst wuerde jeder Klick auf das Label mit einem Mini-Drag kollidieren.
 * Mit der Tastatur verschieben Pfeil hoch und runter auf dem Griff.
 */
const SortableProfileItem = ({
  value,
  label,
  sortable,
  disabled,
  onMove,
  onDragStart,
  onDragEnd,
  children
}) => {
  const { t } = useTranslation()
  const controls = useDragControls()

  const handleKeyDown = (event) => {
    const delta = KEY_MOVES[event.key]
    if (delta === undefined) return

    event.preventDefault()
    onMove(delta)
  }

  return (
    <Reorder.Item
      as='li'
      value={value}
      dragListener={false}
      dragControls={controls}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      whileDrag={{
        scale: 1.02,
        boxShadow:
          '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
      }}
      className='flex items-center rounded-md bg-white hover:bg-gray-50'
    >
      {children}
      {sortable && (
        <button
          type='button'
          disabled={disabled}
          aria-label={t(I18N + 'pdf_bundle_move_profile', { profile: label })}
          title={t(I18N + 'pdf_bundle_drag_hint')}
          onPointerDown={(event) => {
            if (disabled) return
            event.preventDefault()
            controls.start(event)
          }}
          onKeyDown={handleKeyDown}
          className='mr-1 flex size-7 shrink-0 cursor-grab touch-none items-center justify-center rounded text-gray-300 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-600 active:cursor-grabbing disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-gray-300'
        >
          <GripIcon className='size-4' />
        </button>
      )}
    </Reorder.Item>
  )
}

SortableProfileItem.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  sortable: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onMove: PropTypes.func.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired
}

const GroupHeading = ({ id, children, action }) => (
  <div className='flex min-h-6 items-center justify-between gap-2 px-2'>
    <h3 id={id} className='text-xs font-medium text-gray-500'>
      {children}
    </h3>
    {action}
  </div>
)

GroupHeading.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  action: PropTypes.node
}

GroupHeading.defaultProps = {
  action: null
}

/**
 * Kompakter Export-Dialog fuer "Als eine PDF laden". Aivy-Profile und die vom
 * Talent hochgeladenen PDFs landen in einer Datei, in der Reihenfolge der Liste.
 *
 * Gemerkt wird, was abgewaehlt ist, nicht was gewaehlt ist. So sind Dokumente,
 * die erst nach dem Oeffnen fertig geladen sind, automatisch mit drin.
 */
const PdfBundleDialog = ({
  open,
  onClose,
  source,
  space,
  career,
  options,
  bundleExport
}) => {
  const { t } = useTranslation()
  const { language } = useLanguageContext()

  const [bundleLanguage, setBundleLanguage] = useState(() =>
    getInitialLanguage(language)
  )
  const [profileOrder, setProfileOrder] = useState(() =>
    parseStoredProfileOrder(localStorage.getItem(PROFILE_ORDER_STORAGE_KEY))
  )
  const [excludedTypes, setExcludedTypes] = useState([])
  const [excludedDocuments, setExcludedDocuments] = useState([])
  const [announcement, setAnnouncement] = useState('')

  const { profileTypes, documents, isLoadingDocuments, isDocumentsError } =
    options

  const isLoading = bundleExport.isLoadingFor(space.id)

  // Reihenfolge der Liste = Reihenfolge der Reports im PDF. Das Backend setzt
  // die Dokumente immer dahinter, deshalb sortieren sich nur die Profile.
  const orderedProfileTypes = sortProfileTypes(profileTypes, profileOrder)
  const orderedProfileTypesRef = useRef(orderedProfileTypes)
  orderedProfileTypesRef.current = orderedProfileTypes
  const dragStartIndexRef = useRef(null)
  const isSortable = orderedProfileTypes.length > 1

  const bundleableDocuments = documents.filter((doc) => doc.bundleable)
  const selectedTypes = orderedProfileTypes.filter(
    (type) => !excludedTypes.includes(type)
  )
  const selectedDocumentNames = bundleableDocuments
    .filter((doc) => !excludedDocuments.includes(doc.name))
    .map((doc) => doc.name)

  const selectedCount = selectedTypes.length + selectedDocumentNames.length
  const totalCount = profileTypes.length + bundleableDocuments.length
  const canSubmit = selectedTypes.length > 0 && !isLoading

  const allDocumentsSelected =
    selectedDocumentNames.length === bundleableDocuments.length

  const profileLabels = {
    [MATCHINGPROFILE]: {
      label: t(I18N + 'matching_profile'),
      description: career?.title
        ? t(I18N + 'matching_profile_addition', { career_title: career.title })
        : undefined
    },
    [TALENTPROFILE]: { label: t(I18N + 'strength_profile_v2') }
  }

  const formatDate = (date) =>
    format(date, 'd. MMMM yyyy', { locale: { de, en }[language] })

  const changeLanguage = (value) => {
    setBundleLanguage(value)
    localStorage.setItem(LANGUAGE_STORAGE_KEY, value)
  }

  // Reorder meldet nur die sichtbaren Profile. Ein Profil, das dieses Talent
  // nicht hat, behaelt seinen Platz hinten in der gemerkten Reihenfolge.
  const reorderProfiles = (nextVisible) => {
    const next = [
      ...nextVisible,
      ...(profileOrder || []).filter((type) => !nextVisible.includes(type))
    ]

    setProfileOrder(next)
    localStorage.setItem(PROFILE_ORDER_STORAGE_KEY, JSON.stringify(next))
  }

  const announcePosition = (type) => {
    const order = orderedProfileTypesRef.current

    setAnnouncement(
      t(I18N + 'pdf_bundle_position_announcement', {
        profile: profileLabels[type].label,
        position: order.indexOf(type) + 1,
        total: order.length
      })
    )
  }

  const moveProfile = (type, delta) => {
    const index = orderedProfileTypes.indexOf(type)
    const next = moveItem(orderedProfileTypes, index, delta)
    if (next === orderedProfileTypes) return

    reorderProfiles(next)
    orderedProfileTypesRef.current = next
    announcePosition(type)
  }

  const toggleAllDocuments = () =>
    setExcludedDocuments(
      allDocumentsSelected ? bundleableDocuments.map((doc) => doc.name) : []
    )

  const submit = async () => {
    if (!canSubmit) return

    const done = await bundleExport.exportBundle({
      space,
      careerId: career?.id,
      diagnosticVersion: career?.diagnostic_version,
      language: bundleLanguage,
      types: selectedTypes,
      documentNames: selectedDocumentNames,
      source
    })

    if (done) onClose()
  }

  // Cmd/Strg+Enter exportiert von ueberall im Dialog, wie in Linear und Notion.
  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey)) return

    event.preventDefault()
    submit()
  }

  const statusText = isLoading
    ? t(I18N + 'pdf_bundle_loading_hint')
    : selectedTypes.length === 0
      ? t(I18N + 'pdf_bundle_profile_required')
      : t(I18N + 'pdf_bundle_selected_count', {
          count: selectedCount,
          total: totalCount
        })

  const renderDocuments = () => {
    if (isLoadingDocuments) {
      return (
        <ul aria-hidden='true' className='mt-1 space-y-1'>
          {[0, 1].map((index) => (
            <li key={index} className='flex items-center gap-3 px-2 py-2'>
              <span className='size-4 rounded bg-gray-100' />
              <span className='size-8 rounded-md bg-gray-100' />
              <span className='h-3 w-40 animate-pulse rounded bg-gray-100' />
            </li>
          ))}
        </ul>
      )
    }

    if (isDocumentsError || documents.length === 0) {
      return (
        <p className='px-2 py-2 text-sm text-gray-400'>
          {isDocumentsError
            ? t(I18N + 'pdf_bundle_documents_error')
            : t(I18N + 'documents_empty')}
        </p>
      )
    }

    return (
      <ul role='list' className='mt-1'>
        {documents.map((doc) => {
          const { Icon, band } = getFileTypeVisual(doc.extension)
          const showQuestion =
            doc.questionLabel && doc.questionLabel !== doc.displayName
          const description = doc.bundleable
            ? [
                showQuestion && doc.questionLabel,
                doc.uploadedAt && formatDate(doc.uploadedAt)
              ]
                .filter(Boolean)
                .join(' · ')
            : t(I18N + 'pdf_bundle_not_pdf')

          return (
            <li
              key={doc.name}
              className={classNames(
                'rounded-md',
                doc.bundleable && 'hover:bg-gray-50'
              )}
            >
              <OptionLabel
                id={`pdf-bundle-document-${doc.name}`}
                checked={
                  doc.bundleable && !excludedDocuments.includes(doc.name)
                }
                disabled={!doc.bundleable}
                onChange={() =>
                  setExcludedDocuments((prev) => toggle(prev, doc.name))
                }
                visual={
                  <span
                    className={classNames(
                      'flex size-8 shrink-0 items-center justify-center rounded-md',
                      band,
                      !doc.bundleable && 'opacity-40'
                    )}
                  >
                    <Icon className='size-4 text-white' aria-hidden='true' />
                  </span>
                }
                label={doc.displayName}
                description={description || undefined}
              />
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <Modal
      open={open}
      setOpen={onClose}
      size='md'
      padding='p-0'
      borderRadius='rounded-xl'
      hideCloseButton
    >
      <div onKeyDown={handleKeyDown}>
        <div className='flex items-start justify-between gap-4 px-5 pt-5'>
          <div>
            <DialogTitle className='text-base font-semibold text-gray-900'>
              {t(I18N + 'attachments_bundle_action')}
            </DialogTitle>
            <Description className='mt-1 text-sm text-gray-500'>
              {t(I18N + 'pdf_bundle_description')}
            </Description>
          </div>
          <button
            type='button'
            onClick={onClose}
            aria-label={t(I18N + 'pdf_bundle_close')}
            className='-mr-1 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-600'
          >
            <XMarkIcon className='size-5' aria-hidden='true' />
          </button>
        </div>

        <fieldset disabled={isLoading} className='space-y-4 px-3 pb-4 pt-4'>
          <div className='flex items-center justify-between gap-3 px-2'>
            <span id='pdf-bundle-language' className='text-sm text-gray-700'>
              {t(I18N + 'pdf_bundle_language')}
            </span>
            <RadioGroup
              value={bundleLanguage}
              onChange={changeLanguage}
              disabled={isLoading}
              aria-labelledby='pdf-bundle-language'
              className='inline-flex rounded-md bg-gray-100 p-0.5'
            >
              {BUNDLE_LANGUAGES.map((option) => (
                <Radio
                  key={option.value}
                  value={option.value}
                  className='cursor-pointer rounded px-2.5 py-1 text-xs font-medium text-gray-500 hover:text-gray-900 focus:outline-none data-[disabled]:cursor-default data-[checked]:bg-white data-[checked]:text-gray-900 data-[checked]:shadow-sm data-[focus]:ring-1 data-[focus]:ring-blue-600'
                >
                  {option.label}
                </Radio>
              ))}
            </RadioGroup>
          </div>

          <section aria-labelledby='pdf-bundle-profiles'>
            <GroupHeading id='pdf-bundle-profiles'>
              {t(I18N + 'pdf_bundle_profiles')}
            </GroupHeading>
            <MotionConfig reducedMotion='user'>
              <Reorder.Group
                as='ul'
                role='list'
                axis='y'
                values={orderedProfileTypes}
                onReorder={reorderProfiles}
                className='mt-1'
              >
                {orderedProfileTypes.map((type) => (
                  <SortableProfileItem
                    key={type}
                    value={type}
                    label={profileLabels[type].label}
                    sortable={isSortable}
                    disabled={isLoading}
                    onMove={(delta) => moveProfile(type, delta)}
                    onDragStart={() => {
                      dragStartIndexRef.current =
                        orderedProfileTypesRef.current.indexOf(type)
                    }}
                    onDragEnd={() => {
                      const moved =
                        orderedProfileTypesRef.current.indexOf(type) !==
                        dragStartIndexRef.current
                      if (moved) announcePosition(type)
                    }}
                  >
                    <OptionLabel
                      id={`pdf-bundle-profile-${type}`}
                      checked={!excludedTypes.includes(type)}
                      onChange={() =>
                        setExcludedTypes((prev) => toggle(prev, type))
                      }
                      visual={
                        <span
                          className='flex size-8 shrink-0 items-center justify-center rounded-md'
                          style={{
                            background: PROFILE_VISUALS[type].background
                          }}
                        >
                          <img
                            src={PROFILE_VISUALS[type].imageSrc}
                            alt=''
                            className='size-5 object-contain'
                          />
                        </span>
                      }
                      label={profileLabels[type].label}
                      description={profileLabels[type].description}
                    />
                  </SortableProfileItem>
                ))}
              </Reorder.Group>
            </MotionConfig>
            <div aria-live='polite' className='sr-only'>
              {announcement}
            </div>
          </section>

          <section aria-labelledby='pdf-bundle-documents'>
            <GroupHeading
              id='pdf-bundle-documents'
              action={
                bundleableDocuments.length > 1 && (
                  <button
                    type='button'
                    onClick={toggleAllDocuments}
                    className='rounded text-xs font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-600 disabled:text-gray-400'
                  >
                    {allDocumentsSelected
                      ? t(I18N + 'pdf_bundle_deselect_all')
                      : t(I18N + 'pdf_bundle_select_all')}
                  </button>
                )
              }
            >
              {t(I18N + 'pdf_bundle_documents')}
            </GroupHeading>
            {renderDocuments()}
          </section>
        </fieldset>

        <div className='flex flex-col gap-3 rounded-b-xl border-t border-gray-200 bg-gray-50 px-5 py-3 sm:flex-row sm:items-center sm:justify-between'>
          {/* Die Zeile steht immer im DOM und wechselt nur ihren Inhalt, sonst
              lesen Screenreader den Hinweis beim Export nicht vor. */}
          <p
            role='status'
            aria-live='polite'
            className={classNames(
              'text-xs',
              !isLoading && selectedTypes.length === 0
                ? 'text-amber-700'
                : 'text-gray-500'
            )}
          >
            {statusText}
          </p>
          <div className='flex shrink-0 justify-end gap-2'>
            <Button.SecondaryBase
              text={
                isLoading
                  ? t(I18N + 'pdf_bundle_close')
                  : t(I18N + 'pdf_bundle_cancel')
              }
              onClick={onClose}
              minWidth='min-w-0'
            />
            <Button.PrimaryBase
              text={t(I18N + 'pdf_bundle_submit')}
              onClick={submit}
              isLoading={isLoading}
              disabled={selectedTypes.length === 0}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}

PdfBundleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  source: PropTypes.string,
  space: PropTypes.object.isRequired,
  career: PropTypes.object,
  options: PropTypes.shape({
    profileTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
    documents: PropTypes.array.isRequired,
    isLoadingDocuments: PropTypes.bool.isRequired,
    isDocumentsError: PropTypes.bool.isRequired
  }).isRequired,
  bundleExport: PropTypes.shape({
    exportBundle: PropTypes.func.isRequired,
    isLoadingFor: PropTypes.func.isRequired
  }).isRequired
}

PdfBundleDialog.defaultProps = {
  source: undefined,
  career: null
}

export default PdfBundleDialog
